import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
	Chip,
	FormControlLabel,
	MenuItem,
	Radio,
	RadioGroup,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import styled from 'styled-components';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { useSnackbar } from '@Providers/useSnackbar';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { FlexLayout } from '@Styled/utilities';
import { ImportLineEntry, parseImportLines } from '../Constants/sourcePatterns';
import {
	videoImportsCapabilities,
	videoImportsCreate,
	videoImportsCreateFolder,
	videoImportsInspectFolder,
	videoImportErrorMessage,
} from '../Slices/VideoImports.slice';
import {
	ImportInclude,
	InspectVideoImportFolderResult,
	VideoImportProvider,
} from '../Types/VideoImports.types';

// Instructor mirror uses "Ask your platform admin" here.
const NOT_CONFIGURED_TOOLTIP = 'Not configured — see Settings → Video Providers';
const YOUTUBE_REMOVED_TEXT = 'YouTube links are not supported';

const SOURCE_LABELS = {
	gdrive: 'Google Drive',
	onedrive: 'OneDrive',
	youtube: 'YouTube',
	direct: 'Direct link',
} as const;

const URL_PLACEHOLDER =
	'Paste Google Drive / OneDrive video or PDF links — or several, one per line. A FOLDER link imports its videos and PDFs, 20 per paste:\n' +
	'https://drive.google.com/file/d/…  ·  https://1drv.ms/…  ·  https://drive.google.com/drive/folders/…';

/** "3 videos + 2 PDFs" for folder chips — empty folders say so explicitly. */
const folderContentsLabel = (inspection: InspectVideoImportFolderResult) => {
	const parts: string[] = [];
	if (inspection.videos > 0)
		parts.push(
			`${inspection.videos} video${inspection.videos === 1 ? '' : 's'}`
		);
	if (inspection.documents > 0)
		parts.push(
			`${inspection.documents} PDF${inspection.documents === 1 ? '' : 's'}`
		);
	return parts.length ? parts.join(' + ') : 'no importable files';
};

const singleChipLabel = (
	entry: ImportLineEntry,
	inspections: Record<string, InspectVideoImportFolderResult | null>
) => {
	const inspection = inspections[entry.url];
	const isFolder = entry.kind === 'folder' || inspection?.isFolder === true;
	if (!isFolder) return SOURCE_LABELS[entry.source!];
	const base = entry.kind === 'folder' ? 'Drive folder' : 'OneDrive folder';
	return inspection?.isFolder
		? `${base} — ${folderContentsLabel(inspection)}`
		: base;
};

type Props = {
	courseId: number;
	onCreated: () => void;
};

const ImportVideoForm: FC<Props> = ({ courseId, onCreated }) => {
	const dispatch = useAppThunkDispatch();
	const { displaySnackbar } = useSnackbar();
	const { capabilities } = useTypedSelector((state) => state.VideoImports);

	const [url, setUrl] = useState('');
	const [title, setTitle] = useState('');
	const [provider, setProvider] = useState<VideoImportProvider>('bunny');
	// Folder pastes only: which kinds to take (single files auto-detect).
	const [include, setInclude] = useState<ImportInclude>('both');
	// Folder-content chip data keyed by URL. Missing = not answered yet;
	// null = inspect failed (chip falls back to the plain label).
	const [inspections, setInspections] = useState<
		Record<string, InspectVideoImportFolderResult | null>
	>({});
	const inspectRequested = useRef<Set<string>>(new Set());
	// null = idle; otherwise a sequential batch is in flight.
	const [batch, setBatch] = useState<{ done: number; total: number } | null>(
		null
	);

	useEffect(() => {
		dispatch(videoImportsCapabilities());
	}, [dispatch]);

	const entries = useMemo(() => parseImportLines(url), [url]);
	// Parallel names: title-box line N names the Nth non-empty URL line. An
	// explicit "| name" on a URL line beats its paired title line (explicit
	// beats positional) — pairing never shifts either way.
	const titleLines = useMemo(
		() =>
			title
				.split('\n')
				.map((line) => line.trim())
				.filter((line) => line.length > 0),
		[title]
	);
	// Debounced folder inspection: Drive folder lines are known folders; ANY
	// OneDrive line might be one (the URL can't tell) — only the server can
	// list either, so the chip's videos/PDFs counts come from inspect-folder.
	useEffect(() => {
		const candidates = entries
			.filter(
				(entry) =>
					entry.valid &&
					(entry.kind === 'folder' || entry.source === 'onedrive')
			)
			.map((entry) => entry.url)
			.filter((lineUrl) => !inspectRequested.current.has(lineUrl));
		if (!candidates.length) return;
		const timer = setTimeout(() => {
			candidates.forEach(async (lineUrl) => {
				if (inspectRequested.current.has(lineUrl)) return;
				inspectRequested.current.add(lineUrl);
				try {
					const { data } = await dispatch(
						videoImportsInspectFolder({ course_id: courseId, url: lineUrl })
					).unwrap();
					setInspections((current) => ({ ...current, [lineUrl]: data }));
				} catch {
					setInspections((current) => ({ ...current, [lineUrl]: null }));
				}
			});
		}, 600);
		return () => clearTimeout(timer);
	}, [entries, courseId, dispatch]);

	const single = entries.length === 1 ? entries[0] : null;
	const invalidEntry = entries.find((entry) => !entry.valid);
	const youtubeBlockedEntry = entries.find(
		(entry) => entry.source === 'youtube'
	);
	// A confirmed folder line: Drive folders by URL shape, OneDrive folders by
	// the inspect answer. Gates the Import Both/Videos/PDFs select.
	const hasFolder = entries.some(
		(entry) =>
			entry.kind === 'folder' || inspections[entry.url]?.isFolder === true
	);
	const documentsOnly = hasFolder && include === 'documents';
	// While capabilities are still loading, leave options enabled — the
	// backend 422s with the same friendly message anyway (defense in depth).
	const providerDisabled = (name: VideoImportProvider) =>
		capabilities ? !capabilities[name] : false;

	const urlHelperText = useMemo(() => {
		if (!url.trim()) return undefined;
		if (invalidEntry)
			return entries.length > 1
				? `Line ${invalidEntry.line} is not a valid URL`
				: 'Enter a valid video or PDF URL (Google Drive, OneDrive, or a direct video URL)';
		if (youtubeBlockedEntry)
			return entries.length > 1
				? `Line ${youtubeBlockedEntry.line}: ${YOUTUBE_REMOVED_TEXT}`
				: YOUTUBE_REMOVED_TEXT;
		if (entries.length > 1)
			return `${entries.length} links ready — names come from "| name" or the source file`;
		return undefined;
	}, [url, entries, invalidEntry, youtubeBlockedEntry]);

	const canSubmit =
		entries.length > 0 &&
		!invalidEntry &&
		!youtubeBlockedEntry &&
		// A PDF-only folder take needs no video provider at all.
		(documentsOnly || !providerDisabled(provider)) &&
		batch === null;

	const submit = async () => {
		if (!canSubmit) return;
		const toSubmit = entries.map((entry, index) => ({
			...entry,
			// "| name" on the URL line wins, then the paired title-box line.
			effectiveTitle: entry.title ?? titleLines[index],
		}));
		setBatch({ done: 0, total: toSubmit.length });
		const failures: { line: number; url: string; title?: string; message: string }[] =
			[];
		let succeeded = 0;
		// Folder takes cap at 20 new files per paste — surface what's left.
		let remaining = 0;
		let unsupported = 0;
		for (const entry of toSubmit) {
			try {
				if (entry.kind === 'folder') {
					// Expands server-side into one row per video; the paired
					// title line (if any) is ignored — folder files keep their
					// own file names.
					const { data } = await dispatch(
						videoImportsCreateFolder({
							course_id: courseId,
							folder_url: entry.url,
							provider,
							include,
						})
					).unwrap();
					succeeded += data.created;
					remaining += data.remaining ?? 0;
					unsupported += data.unsupported ?? 0;
				} else {
					const { data } = await dispatch(
						videoImportsCreate({
							course_id: courseId,
							source_url: entry.url,
							provider,
							// A OneDrive line might BE a folder — include rides along.
							...(entry.source === 'onedrive' ? { include } : {}),
							...(entry.effectiveTitle ? { title: entry.effectiveTitle } : {}),
						})
					).unwrap();
					// A OneDrive folder link expands server-side into many imports.
					if ('created' in data) {
						succeeded += data.created;
						remaining += data.remaining ?? 0;
						unsupported += data.unsupported ?? 0;
					} else {
						succeeded += 1;
					}
				}
			} catch (error: any) {
				failures.push({
					line: entry.line,
					url: entry.url,
					title: entry.effectiveTitle,
					message: videoImportErrorMessage(
						error,
						'Unable to start the video import'
					),
				});
			}
			setBatch((current) =>
				current ? { ...current, done: current.done + 1 } : current
			);
		}
		setBatch(null);
		if (succeeded > 0) {
			// A folder line can expand to many imports, so count imports, not lines.
			displaySnackbar(
				'success',
				(succeeded === 1 ? 'Import started' : `${succeeded} imports started`) +
					(remaining > 0
						? ` — ${remaining} more in the folder, paste the link again to continue`
						: '') +
					(unsupported > 0
						? ` — ${unsupported} OneDrive shortcut${
								unsupported === 1 ? '' : 's'
						  } can't be imported`
						: '')
			);
		} else if (unsupported > 0 && failures.length === 0) {
			displaySnackbar(
				'error',
				`${unsupported} OneDrive shortcut${
					unsupported === 1 ? '' : 's'
				} can't be imported — share the original file${
					unsupported === 1 ? '' : 's'
				} and import directly`
			);
		}
		if (succeeded > 0 || unsupported > 0) {
			onCreated();
		}
		if (failures.length > 0) {
			displaySnackbar(
				'error',
				failures.length === 1
					? failures[0].message
					: `${failures.length} imports failed — first: line ${failures[0].line}: ${failures[0].message}`
			);
		}
		// Keep only the failed lines for a one-edit retry (their names ride
		// along as "url | name" so pairing can't drift); clear on full success.
		setUrl(
			failures
				.map((failure) =>
					failure.title ? `${failure.url} | ${failure.title}` : failure.url
				)
				.join('\n')
		);
		setTitle('');
	};

	const renderProviderOption = (
		value: VideoImportProvider,
		label: string
	) => {
		const disabled = documentsOnly || providerDisabled(value);
		const tooltip = documentsOnly
			? 'Not used for a PDF-only import'
			: providerDisabled(value)
			? NOT_CONFIGURED_TOOLTIP
			: '';
		return (
			<Tooltip title={tooltip}>
				<span>
					<FormControlLabel
						value={value}
						label={label}
						disabled={disabled}
						control={<Radio />}
					/>
				</span>
			</Tooltip>
		);
	};

	return (
		<FlexLayout flexDirection="column" gridGap="1.5rem" width="100%">
			<FlexLayout alignItems="flex-start" gridGap="1rem">
				<TextField
					fullWidth
					size="small"
					multiline
					maxRows={8}
					label={entries.length > 1 ? `URLs (${entries.length})` : 'Video / PDF URL'}
					placeholder={URL_PLACEHOLDER}
					value={url}
					onChange={(event) => setUrl(event.target.value)}
					error={!!url.trim() && (!!invalidEntry || !!youtubeBlockedEntry)}
					helperText={urlHelperText}
					InputProps={{
						endAdornment:
							single && single.source ? (
								<SourceChip
									size="small"
									label={singleChipLabel(single, inspections)}
									$muted={!!youtubeBlockedEntry}
									color={youtubeBlockedEntry ? 'default' : 'primary'}
									variant="outlined"
								/>
							) : entries.length > 1 && !invalidEntry ? (
								<SourceChip
									size="small"
									label={`${entries.length} files`}
									color="primary"
									variant="outlined"
								/>
							) : undefined,
					}}
				/>
			</FlexLayout>
			<TextField
				fullWidth
				size="small"
				multiline
				maxRows={8}
				label={
					entries.length > 1
						? 'Titles (optional — line 1 names file 1, line 2 names file 2…)'
						: 'Title (optional)'
				}
				placeholder="Defaults to the source file name"
				value={title}
				onChange={(event) => setTitle(event.target.value)}
				helperText={
					titleLines.length > 0 && titleLines.length !== entries.length && entries.length > 0
						? titleLines.length < entries.length
							? `${titleLines.length} name${titleLines.length === 1 ? '' : 's'} for ${entries.length} files — the rest use their file names`
							: `${titleLines.length - entries.length} extra name${titleLines.length - entries.length === 1 ? '' : 's'} ignored (only ${entries.length} file${entries.length === 1 ? '' : 's'} listed)`
						: undefined
				}
			/>
			<FlexLayout alignItems="center" justifyContent="space-between">
				<FlexLayout alignItems="center" gridGap="1.5rem">
					<FlexLayout flexDirection="column">
						<RadioCaption variant="caption">Video provider</RadioCaption>
						<RadioGroup
							row
							value={provider}
							onChange={(event, value) =>
								setProvider(value as VideoImportProvider)
							}
						>
							{renderProviderOption('bunny', 'Bunny Stream')}
							{renderProviderOption('vdocipher', 'VdoCipher')}
						</RadioGroup>
					</FlexLayout>
					{hasFolder && (
						<TextField
							select
							size="small"
							label="Import"
							value={include}
							onChange={(event) =>
								setInclude(event.target.value as ImportInclude)
							}
							sx={{ minWidth: 150 }}
						>
							<MenuItem value="both">Videos + PDFs</MenuItem>
							<MenuItem value="videos">Videos only</MenuItem>
							<MenuItem value="documents">PDFs only</MenuItem>
						</TextField>
					)}
				</FlexLayout>
				<RoundButton
					variant="contained"
					disabled={!canSubmit}
					startIcon={<EdIcon>cloud_download</EdIcon>}
					onClick={submit}
				>
					{batch
						? `Importing ${Math.min(batch.done + 1, batch.total)}/${batch.total}…`
						: entries.length > 1
						? `Import ${entries.length} files`
						: 'Import'}
				</RoundButton>
			</FlexLayout>
		</FlexLayout>
	);
};

const RadioCaption = styled(Typography)`
	color: rgba(0, 0, 0, 0.6);
	line-height: 1;
`;

const SourceChip = styled(Chip)<{ $muted?: boolean }>`
	margin-left: 0.5rem;
	opacity: ${(props) => (props.$muted ? 0.5 : 1)};
`;

export default ImportVideoForm;
